import { ApolloClient, ApolloLink, createHttpLink, from, InMemoryCache, TypePolicies } from "@apollo/client";
import { onError } from "@apollo/client/link/error";
import { RetryLink } from "@apollo/client/link/retry";
import * as Sentry from "@sentry/vue";

import config from "@/config";
import useNotifications from "@/hooks/notifications";
import useAuthentication from "@/hooks/use-authentication";

import { cancelRequestLink } from "./cancelRequest";
import { GRAPHQL_ERROR_FORBIDDEN, GRAPHQL_ERROR_UNAUTHENTICATED } from "./constants";
import typePolicies from "./generated-type-policies";

export * from "@/graphql/utils";
export * from "./generated-types";

const authLink = new ApolloLink((operation, forward) => {
  operation.setContext(({ headers }: { headers: Record<string, unknown> }) => {
    const { accessToken } = useAuthentication();
    return {
      headers: {
        Authorization: `Bearer ${accessToken.value}`,
        ...headers,
      },
    };
  });
  return forward(operation);
});

export interface GraphQLError {
  extensions: {
    code: string;
  };
}

const retryAuthLink = new RetryLink({
  delay: { initial: 300, max: Infinity, jitter: false },
  attempts: async (count, operation, error) => {
    if (error.statusCode === 400) {
      const graphqlErrors = error.result?.errors;
      const hasAuthenticationError = !!graphqlErrors.find(
        (err: GraphQLError) => err.extensions?.code === GRAPHQL_ERROR_UNAUTHENTICATED
      );
      if (hasAuthenticationError) {
        if (count > 3) {
          const { logout, login } = useAuthentication();
          await logout();
          await login(window.location.pathname);
          return true;
        }
        const { refreshAccessToken } = useAuthentication();
        const token = await refreshAccessToken();
        operation.setContext(({ headers }: { headers: Record<string, unknown> }) => ({
          headers: {
            ...headers,
            Authorization: `Bearer ${token}`,
          },
        }));

        return true;
      }

      const isForbiddenError = !!graphqlErrors.find(
        (err: GraphQLError) => err.extensions?.code === GRAPHQL_ERROR_FORBIDDEN
      );
      if (isForbiddenError) {
        window.location.replace(`/forbidden?next=${window.location.pathname}`);
        return true;
      }
    }
    return false;
  },
});

const errorLink = onError(({ graphQLErrors }) => {
  if (graphQLErrors && graphQLErrors.length > 0) {
    const hasAuthError = graphQLErrors.some((error) =>
      [GRAPHQL_ERROR_UNAUTHENTICATED, GRAPHQL_ERROR_FORBIDDEN].includes(error.extensions?.code)
    );

    if (hasAuthError) {
      /* This case is filled via retryAuthLink */
      return;
    }
    const { notifyError } = useNotifications();
    notifyError(
      "Something went wrong with your request. Please, try again later and contact us if the problem persists."
    );
    if (config.environment === "local") {
      console.error(new Error("GraphqlError"), { extra: { errors: graphQLErrors } });
    } else {
      Sentry.captureException(new Error("GraphqlError"), { extra: { errors: JSON.stringify(graphQLErrors, null, 2) } });
    }
  }
});

const httpLink = createHttpLink({
  uri: config.graphqlEndpointUrl,
});

export default new ApolloClient({
  link: from([errorLink, retryAuthLink, authLink, cancelRequestLink, httpLink]),
  cache: new InMemoryCache({
    typePolicies: typePolicies as TypePolicies,
  }),
  connectToDevTools: true,
  defaultOptions: {
    query: {
      fetchPolicy: "network-only",
      errorPolicy: "all",
    },
  },
});
